// RK ship-by / "we'll post it by" estimator (customiser copy).
//
// KEEP IN SYNC with the theme asset (_rk-sg-theme/assets/rk-shipby.js) and the
// checkout extension (extensions/checkout-ship-by). Same rules everywhere:
//   - Standard = 10 business days, Rush = 5 business days.
//   - Business days skip weekends AND VIC weekday public holidays (below).
//   - Melbourne midday cutoff: before 12:00 on a business day => today is day 1;
//     at/after 12:00 or on a weekend/holiday => next business day is day 1.
//   - Day 1 is inclusive, so posted-by = the Nth business day.

const STD_DAYS = 10;
const RUSH_DAYS = 5;
const CUTOFF_HOUR = 12;
const TZ = "Australia/Melbourne";

// VIC public holidays that land on a WEEKDAY (weekend ones are already skipped).
// Keyed "YYYY-M-D" (month + day not zero-padded).
const HOLIDAYS: Record<string, 1> = {
  "2026-1-1": 1, "2026-1-26": 1, "2026-3-9": 1, "2026-4-3": 1, "2026-4-6": 1,
  "2026-6-8": 1, "2026-9-25": 1, "2026-11-3": 1, "2026-12-25": 1, "2026-12-28": 1,
  "2027-1-1": 1, "2027-1-26": 1,
};

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type Cal = { y: number; m: number; d: number };

function isBiz(y: number, m: number, d: number): boolean {
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  if (dow === 0 || dow === 6) return false;
  if (HOLIDAYS[`${y}-${m}-${d}`]) return false;
  return true;
}

function addCal(y: number, m: number, d: number, n: number): Cal {
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function nextBiz(c: Cal): Cal {
  let x = addCal(c.y, c.m, c.d, 1);
  while (!isBiz(x.y, x.m, x.d)) x = addCal(x.y, x.m, x.d, 1);
  return x;
}

function melbNow(): { y: number; m: number; d: number; hour: number } {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: TZ, year: "numeric", month: "numeric", day: "numeric",
      hour: "numeric", hour12: false,
    }).formatToParts(new Date());
    const o: Record<string, number> = {};
    for (const p of parts) if (p.type !== "literal") o[p.type] = parseInt(p.value, 10);
    const hour = o.hour === 24 ? 0 : o.hour;
    return { y: o.year, m: o.month, d: o.day, hour };
  } catch {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate(), hour: n.getHours() };
  }
}

function fmt(c: Cal): string {
  const dow = new Date(Date.UTC(c.y, c.m - 1, c.d)).getUTCDay();
  return `${WD[dow]} ${c.d} ${MO[c.m - 1]}`;
}

export function computeShipBy(mode: "standard" | "rush"): { mode: "standard" | "rush"; label: string; iso: string } {
  const now = melbNow();
  let start: Cal =
    isBiz(now.y, now.m, now.d) && now.hour < CUTOFF_HOUR
      ? { y: now.y, m: now.m, d: now.d }
      : nextBiz(now);
  const n = mode === "rush" ? RUSH_DAYS : STD_DAYS;
  let cur = start;
  for (let count = 1; count < n; count++) cur = nextBiz(cur);
  const iso = `${cur.y}-${String(cur.m).padStart(2, "0")}-${String(cur.d).padStart(2, "0")}`;
  return { mode, label: fmt(cur), iso };
}
